import { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { sb } from '../lib/supabase';
import { fmt } from '../lib/helpers';
import { canSendProfileInvite, patchTeamChange, profileDisplayStatus, subTeamsForTeam, inviteActionLabel, inviteInstructions } from '../lib/profiles';
import { invokeAuth0EdgeFunction } from '../lib/edgeFunctions';
import { useSupabaseAuth, useAccess } from '../context/AuthContext';
import Badge from '../components/Badge';
import MetricCard from '../components/MetricCard';
import Loader from '../components/Loader';
import Modal from '../components/Modal';

const PLATFORM_ROLE_BADGE = { admin: 'pu', member: 'bl', viewer: '' };
const PLATFORM_ROLE_LABEL = { admin: 'Admin', member: 'Member', viewer: 'Viewer' };

function normalizePlatformRole(role) {
  if (role === 'consultant') return 'member';
  return role || 'member';
}

const activeLookups = (items) => (items || []).filter((x) => x.is_active !== false);

const BLANK_INVITE = {
  email: '', full_name: '', platform_role: 'member',
  organisation_id: '', department_id: '',
};

function blankAddPerson(defaultOrgId = '') {
  return {
    organisation_id: defaultOrgId,
    status: 'named',
    full_name: '',
    preferred_name: '',
    display_label: '',
    email: '',
    phone: '',
    job_title: '',
    user_type: 'employee',
    team_id: '',
    sub_team_id: '',
    country_id: '',
    location: '',
    currency_code: '',
    blended_rate: '',
    employment_status: 'active',
  };
}

function Avatar({ name }) {
  const parts = (name || '?').trim().split(' ');
  const initials = parts.length >= 2
    ? parts[0][0] + parts[parts.length - 1][0]
    : parts[0].slice(0, 2);
  return (
    <div className="ua-avatar">
      {initials.toUpperCase()}
    </div>
  );
}

function FormSection({ title, children }) {
  return (
    <div className="ua-form-section">
      <div className="ua-form-section-title">{title}</div>
      {children}
    </div>
  );
}

function field(label, children, hint) {
  return (
    <div className="form-row">
      <label className="form-label">{label}</label>
      {children}
      {hint && <span style={{ fontSize: 10, color: '#888780' }}>{hint}</span>}
    </div>
  );
}

// ── Invite modal ──────────────────────────────────────────────────────────────
function InviteModal({ orgs, functions, onSave, onCancel, saving, error }) {
  const [form, setForm] = useState(BLANK_INVITE);
  const orgFunctions = functions.filter((f) => f.organisation_id === form.organisation_id);

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-hdr">
          <span>Invite new user</span>
          <button type="button" className="modal-close" onClick={onCancel}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-2col">
            {field('First & last name *',
              <input className="form-input" placeholder="Jane Smith"
                value={form.full_name}
                onChange={(e) => setForm((p) => ({ ...p, full_name: e.target.value }))} />
            )}
            {field('Email address *',
              <input className="form-input" type="email" placeholder="jane@example.com"
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
            )}
          </div>
          <div className="form-2col">
            {field('Organisation *',
              <select className="form-select" value={form.organisation_id}
                onChange={(e) => setForm((p) => ({ ...p, organisation_id: e.target.value, department_id: '' }))}>
                <option value="">Select organisation…</option>
                {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            )}
            {field('Platform role *',
              <select className="form-select" value={form.platform_role}
                onChange={(e) => setForm((p) => ({ ...p, platform_role: e.target.value }))}>
                <option value="admin">Admin</option>
                <option value="member">Member</option>
                <option value="viewer">Viewer</option>
              </select>,
              'Access control — separate from user type (employment)'
            )}
          </div>
          {field('Department',
            <select className="form-select" value={form.department_id}
              disabled={!form.organisation_id}
              onChange={(e) => setForm((p) => ({ ...p, department_id: e.target.value }))}>
              <option value="">No department assigned</option>
              {orgFunctions.map((fn) => <option key={fn.id} value={fn.id}>{fn.name}</option>)}
            </select>,
            !form.organisation_id ? 'Select an organisation first' : ''
          )}
          <div className="ua-invite-note">
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"><circle cx="7" cy="7" r="5.5" /><path d="M7 6.5v3M7 4.5h.01" /></svg>
            Creates a pending account and sends an invite email with an Auth0 sign-in link.
          </div>
        </div>
        {error && <div className="form-error" style={{ margin: '0 20px' }}>{error}</div>}
        <div className="modal-ftr">
          <button type="button" className="btn-ghost" onClick={onCancel} disabled={saving}>Cancel</button>
          <button type="button" className="btn-primary" disabled={saving || !form.email.trim() || !form.full_name.trim() || !form.organisation_id}
            onClick={() => onSave(form)}>
            {saving ? 'Sending invite…' : 'Send invite'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Add person modal (Named / Vacant — no invite) ─────────────────────────────
function AddPersonModal({ orgs, lookups, defaultOrgId, isAdmin, onSave, onCancel, saving, error }) {
  const [f, setF] = useState(() => blankAddPerson(defaultOrgId));
  const set = (patch) => setF((p) => ({ ...p, ...patch }));
  const orgId = f.organisation_id;
  const orgTeams = activeLookups(lookups.teams.filter((t) => t.organisation_id === orgId));
  const orgCountries = activeLookups(lookups.countries.filter((c) => c.organisation_id === orgId));
  const nameRequired = f.status !== 'vacant';
  const labelRequired = f.status === 'vacant';

  return (
    <Modal title="Add person" onClose={onCancel} busy={saving}
      footer={<>
        <button type="button" className="btn-ghost" onClick={onCancel} disabled={saving}>Cancel</button>
        <button type="button" className="btn-primary" onClick={() => onSave(f)}
          disabled={saving || !orgId || (nameRequired && !f.full_name.trim()) || (labelRequired && !f.display_label.trim())}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </>}>
      <div className="form-row">
        <label className="form-label">Organisation *</label>
        <select className="form-select" value={f.organisation_id}
          onChange={(e) => set({ organisation_id: e.target.value, team_id: '', sub_team_id: '', country_id: '' })}>
          <option value="">Select organisation…</option>
          {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      </div>
      <div className="form-row">
        <label className="form-label">Lifecycle status</label>
        <select className="form-select" value={f.status} onChange={(e) => set({ status: e.target.value })}>
          <option value="named">Named</option>
          <option value="vacant">Vacant</option>
        </select>
      </div>
      {f.status === 'vacant' ? (
        <div className="form-row">
          <label className="form-label">Display label *</label>
          <input className="form-input" value={f.display_label} onChange={(e) => set({ display_label: e.target.value })}
            placeholder="Configurator — FI Stream, TBC" />
        </div>
      ) : (
        <div className="form-2col">
          <div className="form-row">
            <label className="form-label">Full name *</label>
            <input className="form-input" value={f.full_name} onChange={(e) => set({ full_name: e.target.value })} />
          </div>
          <div className="form-row">
            <label className="form-label">Preferred name</label>
            <input className="form-input" value={f.preferred_name} onChange={(e) => set({ preferred_name: e.target.value })}
              placeholder="Optional display name" />
          </div>
        </div>
      )}
      {f.status !== 'vacant' && (
        <div className="form-2col">
          <div className="form-row">
            <label className="form-label">Email</label>
            <input className="form-input" type="email" value={f.email} onChange={(e) => set({ email: e.target.value })}
              placeholder="Optional — invite later from Edit" />
          </div>
          <div className="form-row">
            <label className="form-label">Phone</label>
            <input className="form-input" type="tel" value={f.phone} onChange={(e) => set({ phone: e.target.value })} />
          </div>
        </div>
      )}
      <div className="form-2col">
        <div className="form-row">
          <label className="form-label">Job title</label>
          <input className="form-input" value={f.job_title} onChange={(e) => set({ job_title: e.target.value })} />
        </div>
        <div className="form-row">
          <label className="form-label">User type</label>
          <select className="form-select" value={f.user_type} onChange={(e) => set({ user_type: e.target.value })}>
            <option value="employee">Employee</option>
            <option value="consultant">Consultant</option>
            <option value="contractor">Contractor</option>
          </select>
        </div>
      </div>
      <div className="form-2col">
        <div className="form-row">
          <label className="form-label">Team</label>
          <select className="form-select" value={f.team_id} disabled={!orgId}
            onChange={(e) => set(patchTeamChange(e.target.value, f.sub_team_id, lookups.subTeams, orgId))}>
            <option value="">—</option>
            {orgTeams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div className="form-row">
          <label className="form-label">Sub-team</label>
          <select className="form-select" value={f.sub_team_id} disabled={!f.team_id}
            onChange={(e) => set({ sub_team_id: e.target.value })}>
            <option value="">{f.team_id ? '—' : 'Select a team first'}</option>
            {subTeamsForTeam(lookups.subTeams, f.team_id, orgId, true, f.sub_team_id).map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="form-2col">
        <div className="form-row">
          <label className="form-label">Country</label>
          <select className="form-select" value={f.country_id} disabled={!orgId}
            onChange={(e) => set({ country_id: e.target.value })}>
            <option value="">—</option>
            {orgCountries.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="form-row">
          <label className="form-label">Location</label>
          <input className="form-input" value={f.location} onChange={(e) => set({ location: e.target.value })}
            placeholder="Offshore, India" />
        </div>
      </div>
      <div className="form-2col">
        <div className="form-row">
          <label className="form-label">Currency</label>
          <input className="form-input" value={f.currency_code} onChange={(e) => set({ currency_code: e.target.value })}
            placeholder="GBP" />
        </div>
        {isAdmin && (
          <div className="form-row">
            <label className="form-label">Blended rate</label>
            <input className="form-input" type="number" value={f.blended_rate}
              onChange={(e) => set({ blended_rate: e.target.value })} />
          </div>
        )}
      </div>
      <div className="form-row">
        <label className="form-label">Employment status</label>
        <select className="form-select" value={f.employment_status} onChange={(e) => set({ employment_status: e.target.value })}>
          <option value="active">Active</option>
          <option value="on_leave">On leave</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>
      <div className="ua-invite-note">
        Creates a Named or Vacant profile only — no email is sent. Use Edit → Send invite when ready.
      </div>
      {error && <div className="form-error" style={{ margin: 0 }}>{error}</div>}
    </Modal>
  );
}

function buildEditForm(user, skillIds = []) {
  return {
    full_name: user.full_name || '',
    preferred_name: user.preferred_name || '',
    email: user.email || '',
    phone: user.phone || '',
    platform_role: normalizePlatformRole(user.platform_role),
    organisation_id: user.organisation_id || '',
    department_id: user.department_id || '',
    functional_role_id: user.functional_role_id || '',
    user_type: user.user_type || '',
    employment_status: user.employment_status || 'active',
    job_title: user.job_title || '',
    country_id: user.country_id || '',
    location: user.location || '',
    blended_rate: user.blended_rate ?? '',
    currency_code: user.currency_code || '',
    team_id: user.team_id || '',
    sub_team_id: user.sub_team_id || '',
    max_availability_pct: user.max_availability_pct ?? '',
    is_primary_contact: !!user.is_primary_contact,
    skill_ids: skillIds,
  };
}

// ── Edit modal ────────────────────────────────────────────────────────────────
function EditModal({
  user, orgs, functions, functionalRoles, lookups, initialSkillIds, isAdmin,
  onSave, onInvite, onCancel, saving, error,
}) {
  const [form, setForm] = useState(() => buildEditForm(user, initialSkillIds));
  const set = (patch) => setForm((p) => ({ ...p, ...patch }));

  const orgId = form.organisation_id || user.organisation_id;
  const orgFunctions = functions.filter((f) => f.organisation_id === orgId);
  const orgRoles = functionalRoles.filter((r) => r.organisation_id === orgId);
  const orgSkills = activeLookups(lookups.skills.filter((s) => s.organisation_id === orgId));

  function toggleSkill(skillId) {
    setForm((p) => {
      const has = p.skill_ids.includes(skillId);
      return { ...p, skill_ids: has ? p.skill_ids.filter((id) => id !== skillId) : [...p.skill_ids, skillId] };
    });
  }

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-hdr">
          <span>Edit — {user.display_label || user.full_name || 'User'}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {canSendProfileInvite(user) && (
              <button type="button" className="btn-ghost sm" disabled={saving} onClick={() => onInvite(form)}>
                {saving ? 'Sending…' : inviteActionLabel(user)}
              </button>
            )}
            <button type="button" className="modal-close" onClick={onCancel}>✕</button>
          </div>
        </div>
        <div className="modal-body">
          {(user.invite_status === 'pending' || canSendProfileInvite(user)) && (
            <div className="ua-invite-note" style={{ marginBottom: 16 }}>
              <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"><circle cx="7" cy="7" r="5.5" /><path d="M7 6.5v3M7 4.5h.01" /></svg>
              <span>
                {user.invite_status === 'pending'
                  ? <>Invited — waiting for first sign-in. {inviteInstructions(form.email || user.email, true)}</>
                  : <>Send invite to email an Auth0 sign-in link to {form.email || user.email || 'this user'}.</>}
              </span>
            </div>
          )}
          <FormSection title="Identity">
            <div className="form-2col">
              <div className="form-row">
                <label className="form-label">Full name</label>
                <input className="form-input" value={form.full_name}
                  onChange={(e) => set({ full_name: e.target.value })} />
              </div>
              <div className="form-row">
                <label className="form-label">Preferred name</label>
                <input className="form-input" value={form.preferred_name}
                  onChange={(e) => set({ preferred_name: e.target.value })}
                  placeholder="Optional display name" />
              </div>
            </div>
            <div className="form-2col">
              <div className="form-row">
                <label className="form-label">Email</label>
                <input className="form-input" type="email" value={form.email}
                  onChange={(e) => set({ email: e.target.value })}
                  placeholder="Required before invite" />
              </div>
              <div className="form-row">
                <label className="form-label">Phone</label>
                <input className="form-input" type="tel" value={form.phone}
                  onChange={(e) => set({ phone: e.target.value })} />
              </div>
            </div>
          </FormSection>

          <FormSection title="Platform access">
            <div className="form-2col">
              <div className="form-row">
                <label className="form-label">Organisation</label>
                <select className="form-select" value={form.organisation_id}
                  onChange={(e) => set({
                    organisation_id: e.target.value,
                    department_id: '',
                    functional_role_id: '',
                    team_id: '',
                    sub_team_id: '',
                  })}>
                  <option value="">No organisation</option>
                  {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>
              <div className="form-row">
                <label className="form-label">Platform role</label>
                <select className="form-select" value={form.platform_role}
                  onChange={(e) => set({ platform_role: e.target.value })}>
                  <option value="admin">Admin</option>
                  <option value="member">Member</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>
            </div>
          </FormSection>

          <FormSection title="Functional">
            {field('Functional role',
              <select className="form-select" value={form.functional_role_id}
                onChange={(e) => set({ functional_role_id: e.target.value })}>
                <option value="">— Not set —</option>
                {orgRoles.map((r) => (
                  <option key={r.id} value={r.id}>{r.full_name || r.name}</option>
                ))}
              </select>,
              'Drives task routing — must match Role Types dictionary'
            )}
          </FormSection>

          <FormSection title="Employment">
            <div className="form-2col">
              <div className="form-row">
                <label className="form-label">Job title</label>
                <input className="form-input" value={form.job_title}
                  onChange={(e) => set({ job_title: e.target.value })} />
              </div>
              <div className="form-row">
                <label className="form-label">User type</label>
                <select className="form-select" value={form.user_type}
                  onChange={(e) => set({ user_type: e.target.value })}>
                  <option value="">— Not set —</option>
                  <option value="employee">Employee</option>
                  <option value="consultant">Consultant</option>
                  <option value="contractor">Contractor</option>
                </select>
              </div>
            </div>
            <div className="form-2col">
              <div className="form-row">
                <label className="form-label">Employment status</label>
                <select className="form-select" value={form.employment_status}
                  onChange={(e) => set({ employment_status: e.target.value })}>
                  <option value="active">Active</option>
                  <option value="on_leave">On leave</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="form-row">
                <label className="form-label">Department</label>
                <select className="form-select" value={form.department_id}
                  disabled={!orgId}
                  onChange={(e) => set({ department_id: e.target.value })}>
                  <option value="">No department assigned</option>
                  {orgFunctions.map((fn) => <option key={fn.id} value={fn.id}>{fn.name}</option>)}
                </select>
              </div>
            </div>
          </FormSection>

          <FormSection title="Resourcing">
            <div className="form-2col">
              <div className="form-row">
                <label className="form-label">Country</label>
                <select className="form-select" value={form.country_id}
                  onChange={(e) => set({ country_id: e.target.value })}>
                  <option value="">—</option>
                  {activeLookups(lookups.countries.filter((c) => c.organisation_id === orgId)).map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <label className="form-label">Location</label>
                <input className="form-input" value={form.location}
                  onChange={(e) => set({ location: e.target.value })}
                  placeholder="e.g. Offshore, India" />
              </div>
            </div>
            <div className="form-2col">
              <div className="form-row">
                <label className="form-label">Currency</label>
                <input className="form-input" value={form.currency_code}
                  onChange={(e) => set({ currency_code: e.target.value })} placeholder="GBP" />
              </div>
              {isAdmin && (
                <div className="form-row">
                  <label className="form-label">Blended rate</label>
                  <input className="form-input" type="number" value={form.blended_rate}
                    onChange={(e) => set({ blended_rate: e.target.value })} />
                </div>
              )}
            </div>
            <div className="form-2col">
              <div className="form-row">
                <label className="form-label">Team</label>
                <select className="form-select" value={form.team_id}
                  onChange={(e) => set(patchTeamChange(e.target.value, form.sub_team_id, lookups.subTeams, orgId))}>
                  <option value="">—</option>
                  {activeLookups(lookups.teams.filter((t) => t.organisation_id === orgId)).map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <label className="form-label">Sub-team</label>
                <select className="form-select" value={form.sub_team_id}
                  disabled={!form.team_id}
                  onChange={(e) => set({ sub_team_id: e.target.value })}>
                  <option value="">{form.team_id ? '—' : 'Select a team first'}</option>
                  {subTeamsForTeam(lookups.subTeams, form.team_id, orgId, true, form.sub_team_id).map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-row">
              <label className="form-label">Max availability %</label>
              <input className="form-input" type="number" min="0" max="100" style={{ maxWidth: 120 }}
                value={form.max_availability_pct}
                onChange={(e) => set({ max_availability_pct: e.target.value })}
                placeholder="e.g. 80" />
              <span style={{ fontSize: 10, color: '#888780' }}>General default — pre-fills programme ceiling, does not replace it</span>
            </div>
            <div className="form-row">
              <label className="form-label">Skills</label>
              <div className="ua-skill-list">
                {orgSkills.length === 0 ? (
                  <span style={{ fontSize: 11, color: '#888780' }}>No skills defined — add under Resource → Setup</span>
                ) : orgSkills.map((s) => (
                  <label key={s.id} className="ua-skill-chip">
                    <input type="checkbox" checked={form.skill_ids.includes(s.id)} onChange={() => toggleSkill(s.id)} />
                    {s.name}
                  </label>
                ))}
              </div>
            </div>
            <label className="ua-check-row">
              <input type="checkbox" checked={form.is_primary_contact}
                onChange={(e) => set({ is_primary_contact: e.target.checked })} />
              Primary contact
            </label>
          </FormSection>
        </div>

        {error && <div className="form-error" style={{ margin: '0 20px' }}>{error}</div>}

        <div className="modal-ftr">
          <button type="button" className="btn-ghost" onClick={onCancel} disabled={saving}>Cancel</button>
          <button type="button" className="btn-primary" onClick={() => onSave(form)} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function UserAdmin() {
  const { profile } = useSupabaseAuth();
  const { getAccessTokenSilently } = useAuth0();
  const { isAdmin } = useAccess();
  const [users, setUsers] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [functionalRoles, setFunctionalRoles] = useState([]);
  const [lookups, setLookups] = useState({ teams: [], subTeams: [], countries: [], skills: [] });
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('all');

  const [inviteOpen, setInviteOpen] = useState(false);
  const [addPersonOpen, setAddPersonOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editSkillIds, setEditSkillIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (profile?.organisation_id) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only reload on org change, not on every `load` identity change
  }, [profile?.organisation_id]);

  async function load() {
    setLoading(true);
    const [
      { data: usersData },
      { data: orgsData },
      { data: fnsData },
      { data: rolesData },
      { data: teams },
      { data: subTeams },
      { data: countries },
      { data: skills },
    ] = await Promise.all([
      sb.from('profiles')
        .select('*, organisations(name), functions:department_id(name), functional_roles(full_name, name)')
        .eq('is_demo_persona', false)
        .eq('organisation_id', profile.organisation_id)
        .order('created_at', { ascending: false }),
      // Scoped to the active org only — this is a single-company admin screen, and
      // an admin who now belongs to more than one company should manage each org's
      // users from that org's own context, not accidentally invite/reassign across
      // companies via a dropdown that happened to list every org they're in.
      sb.from('organisations').select('id, name').eq('id', profile.organisation_id).eq('is_template', false).order('name'),
      sb.from('functions').select('id, name, organisation_id').order('name'),
      sb.from('functional_roles').select('id, name, full_name, organisation_id').order('order_index'),
      sb.from('resource_teams').select('id, name, organisation_id, is_active').order('name'),
      sb.from('resource_sub_teams').select('id, name, organisation_id, team_id, is_active').order('name'),
      sb.from('resource_countries').select('id, name, organisation_id').order('name'),
      sb.from('resource_skills').select('id, name, organisation_id').order('name'),
    ]);
    setUsers(usersData || []);
    setOrgs(orgsData || []);
    setFunctions(fnsData || []);
    setFunctionalRoles(rolesData || []);
    setLookups({
      teams: teams || [],
      subTeams: subTeams || [],
      countries: countries || [],
      skills: skills || [],
    });
    setLoading(false);
  }

  async function openEdit(user) {
    setError(null);
    const orgId = user.organisation_id;
    const [{ data: skillRows }, { data: teams }, { data: subTeams }] = await Promise.all([
      sb.from('profile_skills').select('skill_id').eq('profile_id', user.id),
      orgId
        ? sb.from('resource_teams').select('id, name, organisation_id, is_active').eq('organisation_id', orgId).order('name')
        : Promise.resolve({ data: [] }),
      orgId
        ? sb.from('resource_sub_teams').select('id, name, organisation_id, team_id, is_active').eq('organisation_id', orgId).order('name')
        : Promise.resolve({ data: [] }),
    ]);
    setEditSkillIds((skillRows || []).map((r) => r.skill_id));
    if (teams || subTeams) {
      setLookups((prev) => ({
        ...prev,
        teams: teams || prev.teams,
        subTeams: subTeams || prev.subTeams,
      }));
    }
    setEditTarget(user);
  }

  function showToast(msg, isError = false) {
    setToast({ msg, isError });
    setTimeout(() => setToast(null), 3500);
  }

  async function handleInvite(form) {
    if (!form.email?.trim()) {
      setError('Email is required to send an invite');
      return;
    }
    setSaving(true);
    setError(null);

    try {
      const data = await invokeAuth0EdgeFunction('invite-user', {
        email: form.email.trim().toLowerCase(),
        full_name: form.full_name.trim(),
        platform_role: normalizePlatformRole(form.platform_role),
        organisation_id: form.organisation_id,
        department_id: form.department_id || null,
      }, getAccessTokenSilently);

      setInviteOpen(false);
      showToast(`${data?.message || 'Invite sent.'} ${inviteInstructions(form.email, data?.email_sent !== false)}`);
      load();
    } catch (e) {
      setError(e?.message || 'Invite failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleAddPerson(form) {
    if (!form.organisation_id) {
      setError('Organisation is required');
      return;
    }
    setSaving(true);
    setError(null);

    const payload = {
      organisation_id: form.organisation_id,
      full_name: form.status === 'vacant' ? null : (form.full_name.trim() || null),
      preferred_name: form.status === 'vacant' ? null : (form.preferred_name.trim() || null),
      status: form.status,
      display_label: form.status === 'vacant' ? (form.display_label.trim() || null) : null,
      job_title: form.job_title.trim() || null,
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      team_id: form.team_id || null,
      sub_team_id: form.sub_team_id || null,
      user_type: form.user_type,
      platform_role: 'member',
      country_id: form.country_id || null,
      location: form.location.trim() || null,
      currency_code: form.currency_code.trim() || null,
      employment_status: form.employment_status,
    };
    if (isAdmin) {
      payload.blended_rate = form.blended_rate === '' ? null : Number(form.blended_rate);
    }

    const { error: insertErr } = await sb.from('profiles').insert(payload);
    setSaving(false);

    if (insertErr) {
      setError(insertErr.message);
      return;
    }

    setAddPersonOpen(false);
    showToast('Person added');
    load();
  }

  async function handleInviteExisting(user, form) {
    if (!form.email?.trim()) {
      setError('Email is required to send an invite');
      return;
    }
    setSaving(true);
    setError(null);

    try {
      const data = await invokeAuth0EdgeFunction('invite-user', {
        profile_id: user.id,
        email: form.email.trim().toLowerCase(),
        full_name: form.full_name.trim() || user.full_name,
        platform_role: normalizePlatformRole(form.platform_role),
        organisation_id: form.organisation_id || user.organisation_id,
        department_id: form.department_id || null,
      }, getAccessTokenSilently);

      setEditTarget(null);
      showToast(`${data?.message || 'Invite sent.'} ${inviteInstructions(form.email, data?.email_sent !== false)}`);
      load();
    } catch (e) {
      setError(e?.message || 'Invite failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleEdit(form) {
    setSaving(true);
    setError(null);

    const payload = {
      full_name: form.full_name.trim() || null,
      preferred_name: form.preferred_name.trim() || null,
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      platform_role: normalizePlatformRole(form.platform_role),
      organisation_id: form.organisation_id || null,
      department_id: form.department_id || null,
      functional_role_id: form.functional_role_id || null,
      user_type: form.user_type || null,
      employment_status: form.employment_status || 'active',
      job_title: form.job_title.trim() || null,
      country_id: form.country_id || null,
      location: form.location.trim() || null,
      currency_code: form.currency_code.trim() || null,
      team_id: form.team_id || null,
      sub_team_id: form.sub_team_id || null,
      max_availability_pct: form.max_availability_pct === '' ? null : Number(form.max_availability_pct),
      is_primary_contact: form.is_primary_contact,
    };
    if (isAdmin) {
      payload.blended_rate = form.blended_rate === '' ? null : Number(form.blended_rate);
    }

    const { error: updateErr } = await sb.from('profiles').update(payload).eq('id', editTarget.id);
    if (updateErr) {
      setSaving(false);
      setError(updateErr.message);
      return;
    }

    await sb.from('profile_skills').delete().eq('profile_id', editTarget.id);
    if (form.skill_ids?.length) {
      const { error: skillErr } = await sb.from('profile_skills').insert(
        form.skill_ids.map((skill_id) => ({ profile_id: editTarget.id, skill_id }))
      );
      if (skillErr) {
        setSaving(false);
        setError(skillErr.message);
        return;
      }
    }

    setSaving(false);
    setEditTarget(null);
    showToast('User updated');
    load();
  }

  const counts = { admin: 0, member: 0, viewer: 0 };
  users.forEach((u) => {
    const role = normalizePlatformRole(u.platform_role);
    if (counts[role] !== undefined) counts[role] += 1;
  });

  const filtered = roleFilter === 'all'
    ? users
    : users.filter((u) => normalizePlatformRole(u.platform_role) === roleFilter);

  return (
    <>
      <div className="topbar">
        <div>
          <div className="tbtitle">User administration</div>
          <div className="tbcrumb">Manage team access, functional roles, and resourcing attributes</div>
        </div>
        <div className="badges">
          <Badge variant="pu">{users.length} user{users.length !== 1 ? 's' : ''}</Badge>
          <button type="button" className="btn-ghost" onClick={() => { setError(null); setAddPersonOpen(true); }}>
            + Add person
          </button>
          <button type="button" className="btn-primary" onClick={() => { setError(null); setInviteOpen(true); }}>
            + Invite user
          </button>
        </div>
      </div>

      <div className="content">
        <div className="m4">
          <MetricCard label="Total users" value={users.length} sub="across all organisations" />
          <MetricCard label="Admins" value={counts.admin} sub="platform administrators" valueColor={counts.admin > 0 ? '#534AB7' : undefined} />
          <MetricCard label="Members" value={counts.member} sub="programme participants" />
          <MetricCard label="Viewers" value={counts.viewer} sub="read-only access" />
        </div>

        <div className="card">
          <div className="chdr">
            <span className="ctitle">All users</span>
            <div className="filter-tabs" style={{ marginBottom: 0 }}>
              {[['all', 'All'], ['admin', 'Admins'], ['member', 'Members'], ['viewer', 'Viewers']].map(([key, label]) => (
                <button key={key} type="button" className={`ftab${roleFilter === key ? ' active' : ''}`}
                  onClick={() => setRoleFilter(key)}>
                  {label}
                  {key !== 'all' && <span style={{ marginLeft: 4, opacity: 0.7 }}>{counts[key] ?? 0}</span>}
                </button>
              ))}
            </div>
          </div>

          {loading ? <Loader /> : filtered.length === 0 ? (
            <div className="empty">
              No users found.{' '}
              <span style={{ color: '#534AB7', cursor: 'pointer' }} onClick={() => setInviteOpen(true)}>Invite one?</span>
            </div>
          ) : (
            <div>
              <div className="ua-header-row">
                <span style={{ flex: '0 0 36px' }} />
                <span style={{ flex: 1.6 }}>Name</span>
                <span style={{ flex: 1.6 }}>Email</span>
                <span style={{ flex: 0.8 }}>Role</span>
                <span style={{ flex: 0.7 }}>Status</span>
                <span style={{ flex: 1.1 }}>Functional role</span>
                <span style={{ flex: 0.9 }}>Department</span>
                <span style={{ flex: 1.2 }}>Organisation</span>
                <span style={{ flex: 0.8 }}>Added</span>
                <span style={{ flex: '0 0 60px' }} />
              </div>

              {filtered.map((u) => {
                const platformRole = normalizePlatformRole(u.platform_role);
                return (
                  <div key={u.id} className="ua-row">
                    <Avatar name={u.display_label || u.full_name} />
                    <div style={{ flex: 1.6, minWidth: 0 }}>
                      <div className="ua-name">{u.display_label || u.full_name || '—'}</div>
                    </div>
                    <div style={{ flex: 1.6, minWidth: 0 }}>
                      <div className="ua-email">{u.email || '—'}</div>
                    </div>
                    <div style={{ flex: 0.8 }}>
                      <Badge variant={PLATFORM_ROLE_BADGE[platformRole] || ''}>
                        {PLATFORM_ROLE_LABEL[platformRole] || platformRole}
                      </Badge>
                    </div>
                    <div style={{ flex: 0.7 }}>
                      <span className="ua-meta" style={{ textTransform: 'capitalize' }}>
                        {profileDisplayStatus(u)}
                      </span>
                    </div>
                    <div style={{ flex: 1.1, minWidth: 0 }}>
                      <span className="ua-meta">{u.functional_roles?.full_name || '—'}</span>
                    </div>
                    <div style={{ flex: 0.9 }}>
                      <span className="ua-meta">{u.functions?.name || '—'}</span>
                    </div>
                    <div style={{ flex: 1.2, minWidth: 0 }}>
                      <span className="ua-meta">{u.organisations?.name || '—'}</span>
                    </div>
                    <div style={{ flex: 0.8 }}>
                      <span className="ua-meta">{fmt(u.created_at)}</span>
                    </div>
                    <div style={{ flex: '0 0 60px', display: 'flex', justifyContent: 'flex-end' }}>
                      <button type="button" className="row-act" onClick={() => openEdit(u)}>Edit</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="ua-deploy-note">
          <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"><circle cx="7" cy="7" r="5.5" /><path d="M7 6.5v3M7 4.5h.01" /></svg>
          <span>
            Bulk CSV import is not built yet (brief §6.3 — planned next).
            Use + Add person for Named/Vacant profiles; + Invite user sends an email with an Auth0 sign-in link.
          </span>
        </div>
      </div>

      {addPersonOpen && (
        <AddPersonModal
          orgs={orgs}
          lookups={lookups}
          defaultOrgId={profile?.organisation_id || ''}
          isAdmin={isAdmin}
          onSave={handleAddPerson}
          onCancel={() => setAddPersonOpen(false)}
          saving={saving}
          error={error}
        />
      )}

      {inviteOpen && (
        <InviteModal
          orgs={orgs}
          functions={functions}
          onSave={handleInvite}
          onCancel={() => setInviteOpen(false)}
          saving={saving}
          error={error}
        />
      )}

      {editTarget && (
        <EditModal
          user={editTarget}
          orgs={orgs}
          functions={functions}
          functionalRoles={functionalRoles}
          lookups={lookups}
          initialSkillIds={editSkillIds}
          isAdmin={isAdmin}
          onSave={handleEdit}
          onInvite={(form) => handleInviteExisting(editTarget, form)}
          onCancel={() => setEditTarget(null)}
          saving={saving}
          error={error}
        />
      )}

      {toast && (
        <div className={toast.isError ? 'toast-error' : 'toast-success'}>
          {toast.msg}
        </div>
      )}
    </>
  );
}
